/**
 * Consumable catalog — the single source of truth is `consumables.store`
 * (repo root); this module imports its generated projection
 * `consumables.data.ts` (do not edit — save in the knobs app's Consumables
 * tab or run `node tools/consumables-store.js`).
 *
 * Consumables are pocket-only (design/loot.md): never worn, used in place
 * (the UseItem op — the sim slice lands after the store/sack/pickup slices),
 * discarded at floor end. Effects are data:
 *   - heal: instant +HP, capped at max HP (amount).
 *   - buff: a percentage multiplier (magnitude, 100 = 1x) over one of four
 *     combat values (buffType) for a duration in TICKS. Same-type buffs
 *     REFRESH, never stack — the newer use sets magnitude and resets
 *     duration.
 *
 * The sim (next slice) resolves these rows; until then the knobs tab authors
 * them and the data module is the projection surface.
 */
import { CONSUMABLES_DATA } from "./consumables.data.js";

export const CONSUMABLE_TYPES = ["heal", "buff"] as const;
export type ConsumableType = (typeof CONSUMABLE_TYPES)[number];

/** The buff families (percentage multipliers, design/loot.md):
 *  damage = outgoing attack damage, damage_taken = incoming damage taken,
 *  speed = movement cadence (never below 1 tick), accuracy = base hit chance. */
export const BUFF_TYPES = ["damage", "damage_taken", "speed", "accuracy"] as const;
export type BuffType = (typeof BUFF_TYPES)[number];

export interface ConsumableDef {
  /** Catalog id — the permanent consumables.store id (wire/state id; the
   *  catalog may have gaps after deletions). */
  id: number;
  /** Display name (HUD, chat). */
  name: string;
  /** Atlas items-section id of the 48x48 icon (the shape, or shape-shade for
   *  shaded items — e.g. "potion-3b82f6"). */
  icon: string;
  /** The icon's shade hex (#rrggbb) or null (the palette's canon colours). */
  shade: string | null;
  type: ConsumableType;
  /** heal: instant +HP (capped at max HP). Null for buffs. */
  amount: number | null;
  /** buff: which combat value the multiplier applies to. Null for heals. */
  buffType: BuffType | null;
  /** buff: the multiplier in percent (100 = 1x). Null for heals. */
  magnitude: number | null;
  /** buff: the duration in TICKS (x TICK_MS = the ms a client displays).
   *  Null for heals. */
  duration: number | null;
}

/**
 * One row of the generated `consumables.data.ts` projection — typed (the
 * data module is annotated with it) so a hand-edited or stale projection
 * that drops a required field is a TYPECHECK error, not just the
 * catalog's load-time throw. The heal/buff nulls are explicit: the emitter
 * always writes every field (the cross-field rules — heal XOR buff fields —
 * are the runtime validator's, not a type's).
 */
export interface ConsumableDataRow {
  id: number;
  name: string;
  icon: string;
  shade: string | null;
  type: ConsumableType;
  amount: number | null;
  buffType: BuffType | null;
  magnitude: number | null;
  duration: number | null;
}

/**
 * Validate consumable records into a catalog — the ONE validation path for
 * both the committed projection (buildCatalog over consumables.data.ts)
 * and scenario fixtures (`config.consumables`): a bad record must fail
 * loudly with a readable message, not type-trick its way past the checks.
 * `label` prefixes every error (the module file or the scenario field).
 */
export function buildConsumableCatalog(records: readonly unknown[], label = "consumables.data"): readonly ConsumableDef[] {
  const ids = new Set<number>();
  const out: ConsumableDef[] = [];
  for (const r of records) {
    const raw = r as {
      id?: unknown; name?: unknown; icon?: unknown; shade?: unknown;
      type?: unknown; amount?: unknown; buffType?: unknown;
      magnitude?: unknown; duration?: unknown;
    };
    if (!Number.isInteger(raw.id) || (raw.id as number) < 0) {
      throw new Error(`${label}: bad id ${String(raw.id)}`);
    }
    const id = raw.id as number;
    if (ids.has(id)) throw new Error(`${label}: duplicate id ${id}`);
    ids.add(id);
    if (typeof raw.name !== "string" || raw.name === "") {
      throw new Error(`${label}: id ${id}: missing name`);
    }
    if (typeof raw.icon !== "string" || raw.icon === "") {
      throw new Error(`${label}: id ${id}: missing icon`);
    }
    let shade: string | null = null;
    if (raw.shade !== null && raw.shade !== undefined) {
      if (typeof raw.shade !== "string" || !/^#[0-9a-f]{6}$/.test(raw.shade)) {
        throw new Error(`${label}: ${raw.name}: bad shade ${String(raw.shade)} (expected #rrggbb or null)`);
      }
      shade = raw.shade;
    }
    if (shade !== null && !raw.icon.endsWith(`-${shade.slice(1)}`)) {
      throw new Error(`${label}: ${raw.name}: icon ${raw.icon} does not carry its shade ${shade}`);
    }
    if (typeof raw.type !== "string" || !(CONSUMABLE_TYPES as readonly string[]).includes(raw.type)) {
      throw new Error(`${label}: ${raw.name}: bad type ${String(raw.type)} (allowed: ${CONSUMABLE_TYPES.join(" | ")})`);
    }
    const type = raw.type as ConsumableType;
    let amount: number | null = null;
    let buffType: BuffType | null = null;
    let magnitude: number | null = null;
    let duration: number | null = null;
    if (type === "heal") {
      if (!Number.isInteger(raw.amount) || (raw.amount as number) < 1) {
        throw new Error(`${label}: ${raw.name}: heal needs amount >= 1`);
      }
      amount = raw.amount as number;
      // `?? null`: an omitted field is an absent field (fixture records and
      // hand modules leave it out; the emitter writes explicit nulls).
      if ((raw.buffType ?? null) !== null || (raw.magnitude ?? null) !== null || (raw.duration ?? null) !== null) {
        throw new Error(`${label}: ${raw.name}: heal cannot carry buff fields`);
      }
    } else {
      if ((raw.amount ?? null) !== null) {
        throw new Error(`${label}: ${raw.name}: buff cannot carry amount`);
      }
      if (typeof raw.buffType !== "string" || !(BUFF_TYPES as readonly string[]).includes(raw.buffType)) {
        throw new Error(`${label}: ${raw.name}: buff needs buffType (${BUFF_TYPES.join(" | ")})`);
      }
      if (!Number.isInteger(raw.magnitude) || (raw.magnitude as number) < 1) {
        throw new Error(`${label}: ${raw.name}: buff needs magnitude >= 1`);
      }
      if (!Number.isInteger(raw.duration) || (raw.duration as number) < 1) {
        throw new Error(`${label}: ${raw.name}: buff needs duration in ticks >= 1`);
      }
      buffType = raw.buffType as BuffType;
      magnitude = raw.magnitude as number;
      duration = raw.duration as number;
    }
    out.push({
      id,
      name: raw.name,
      icon: raw.icon,
      shade,
      type,
      amount,
      buffType,
      magnitude,
      duration,
    });
  }
  return out;
}

/**
 * All consumables, validated at load with the same discipline the equipment
 * catalog uses: a hand-edited consumables.data.ts must fail loudly.
 */
function buildCatalog(): readonly ConsumableDef[] {
  return buildConsumableCatalog(CONSUMABLES_DATA);
}

/** All consumables, by catalog id (may have gaps — permanent ids). */
export const CONSUMABLES: readonly ConsumableDef[] = buildCatalog();

const BY_ID = new Map(CONSUMABLES.map((c) => [c.id, c]));

/** Catalog lookup; undefined for unknown ids (the sim rejects those with an
 *  invalid event — decode stays loose, the sim stays strict). */
export function consumableById(id: number): ConsumableDef | undefined {
  return BY_ID.get(id);
}
